export const CORE_RULE_KEYS = [
  "gameMode",
  "matchDuration",
  "format",
  "tieDecision",
  "teamSelection",
  "platform",
];

export const CREATOR_GAME_MODE_OPTIONS = ["ultimate_team", "kick_off"];
export const CREATOR_TEAM_CHOICE_OPTIONS = ["free", "custom"];
export const CREATOR_PLATFORM_OPTIONS = [
  "all",
  "playstation_new_gen",
  "xbox_new_gen",
  "pc",
];

export const CREATOR_ADDITIONAL_RULES_TITLE_KEY = "competitionRules.creatorRulesTitle";
export const TOURNAMENT_ADDITIONAL_RULES_TITLE_KEY = "competitionRules.tournamentAdditionalRulesTitle";
export const ADDITIONAL_TOURNAMENT_RULE_LABEL_KEY = "competitionRules.additionalTournamentRule";

export function getLocalizedRuleText(value, language, fallback = "") {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return fallback;
  return (
    value[language] ||
    value.de ||
    value.en ||
    Object.values(value).find((item) => typeof item === "string") ||
    fallback
  );
}

export function getRuleLabelText(rule, language, fallback = "") {
  if (typeof rule === "string") return rule;
  return getLocalizedRuleText(rule?.label || rule?.title || rule?.name, language, fallback);
}

export function getRuleValueText(rule, language, fallback = "") {
  if (typeof rule === "string") return "";
  return getLocalizedRuleText(rule?.value || rule?.description || rule?.text, language, fallback);
}

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase().replace(/[_\s-]+/g, "_");
}

export function normalizeCreatorGameMode(value) {
  const normalized = normalizeToken(value);
  if (["kick_off", "kickoff", "anstoß", "anstoss"].includes(normalized)) return "kick_off";
  return "ultimate_team";
}

export function normalizeCreatorTeamChoiceMode(value) {
  return normalizeToken(value) === "custom" ? "custom" : "free";
}

export function normalizeCreatorPlatform(value) {
  const normalized = normalizeToken(value);
  if (["playstation", "ps", "ps5", "playstation_new_gen"].includes(normalized)) return "playstation_new_gen";
  if (["xbox", "xbox_new_gen"].includes(normalized)) return "xbox_new_gen";
  if (normalized === "pc") return "pc";
  return "all";
}

export function isTournamentGroupStageEnabled(tournament = {}) {
  const format = normalizeToken(tournament.format || tournament.tournamentFormat);
  const groupStageStatus = normalizeToken(tournament.groupStageStatus);
  const knockoutStatus = normalizeToken(tournament.knockoutStatus);

  return (
    tournament.groupStageEnabled === true ||
    tournament.groupStage?.enabled === true ||
    format === "group_then_knockout" ||
    format === "group_stage" ||
    format === "groupstage" ||
    groupStageStatus === "not_started" ||
    groupStageStatus === "running" ||
    groupStageStatus === "completed" ||
    knockoutStatus === "waiting_for_groups"
  );
}

export function getCoreRuleValue(key, tournament = {}, language = "de", t = null) {
  const translator = typeof t === "function" ? t : ((translationKey, fallback) => fallback || translationKey);
  const ruleConfig = tournament?.ruleConfig || tournament?.creatorRuleConfig || {};
  const gameMode = normalizeCreatorGameMode(ruleConfig.gameMode || tournament.gameMode);
  const teamChoiceMode = normalizeCreatorTeamChoiceMode(ruleConfig.teamChoiceMode || tournament.teamChoiceMode);
  const teamChoiceText = String(ruleConfig.teamChoiceText || tournament.teamChoiceText || "").trim();
  const platform = normalizeCreatorPlatform(ruleConfig.platform || tournament.rulesPlatform || tournament.platform);
  const hasGroupStage = isTournamentGroupStageEnabled(tournament);

  if (key === "gameMode") {
    return translator(`competitionRules.values.gameMode.${gameMode}`);
  }
  if (key === "matchDuration") {
    return translator("competitionRules.values.matchDuration");
  }
  if (key === "format") {
    return hasGroupStage
      ? translator("competitionRules.values.format.groupStage")
      : translator("competitionRules.values.format.knockout");
  }
  if (key === "tieDecision") {
    return translator("competitionRules.values.tieDecision");
  }
  if (key === "teamSelection") {
    if (teamChoiceMode === "custom" && teamChoiceText) return teamChoiceText;
    return translator("competitionRules.values.teamSelection.free");
  }
  if (key === "platform") {
    return translator(`competitionRules.values.platform.${platform}`);
  }

  return "";
}

export function buildCoreTournamentRules(tournament = {}, language = "de", t = null) {
  const translator = typeof t === "function" ? t : ((translationKey, fallback) => fallback || translationKey);
  return CORE_RULE_KEYS.map((key) => ({
    key,
    label: translator(`competitionRules.labels.${key}`),
    value: getCoreRuleValue(key, tournament, language, translator),
  }));
}

function normalizeCreatorRule(rule, language, t = null) {
  const translator = typeof t === "function" ? t : ((translationKey, fallback) => fallback || translationKey);
  const additionalRuleLabel = translator(ADDITIONAL_TOURNAMENT_RULE_LABEL_KEY);
  if (typeof rule === "string") {
    const text = rule.trim();
    return text ? { label: additionalRuleLabel, value: text } : null;
  }
  const value = getRuleValueText(rule, language, "").trim();
  const label = additionalRuleLabel || getRuleLabelText(rule, language, "").trim();
  if (!label && !value) return null;
  return { label, value };
}

function buildAdditionalRulesFromSources(rawRules, customRules, language, t = null) {
  if (customRules) {
    rawRules.push(
      ...customRules
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
    );
  }

  const seen = new Set();
  return rawRules
    .map((rule) => normalizeCreatorRule(rule, language, t))
    .filter(Boolean)
    .filter((rule) => {
      const key = String(rule.value || rule.label || "")
        .trim()
        .toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);
}

function usesTournamentAdditionalRules(tournament = {}) {
  const explicitType = normalizeToken(tournament.additionalRulesType || tournament.rulesOwnerType);
  if (["tournament", "special", "special_cup"].includes(explicitType)) return true;

  const tournamentId = normalizeToken(tournament.tournamentId || tournament.type);
  return tournamentId === "special" && !tournament.creatorUid && !tournament.createdFromRequest;
}

export function getAdditionalCreatorRules(tournament = {}, language = "de", t = null) {
  const rawRules = [
    ...(Array.isArray(tournament.creatorRules) ? tournament.creatorRules : []),
    ...(Array.isArray(tournament.additionalCreatorRules) ? tournament.additionalCreatorRules : []),
    ...(Array.isArray(tournament.selectedStandardRules) ? tournament.selectedStandardRules : []),
  ];
  const customRules = String(tournament.customRules || tournament.creatorCustomRules || "").trim();
  return buildAdditionalRulesFromSources(rawRules, customRules, language, t);
}

export function getAdditionalTournamentRules(tournament = {}, language = "de", t = null) {
  const rawRules = [
    ...(Array.isArray(tournament.tournamentRules) ? tournament.tournamentRules : []),
    ...(Array.isArray(tournament.additionalTournamentRules) ? tournament.additionalTournamentRules : []),
    ...(Array.isArray(tournament.specialCupRules) ? tournament.specialCupRules : []),
  ];
  const customRules = String(
    tournament.customRules ||
    tournament.tournamentCustomRules ||
    tournament.specialCupCustomRules ||
    ""
  ).trim();
  return buildAdditionalRulesFromSources(rawRules, customRules, language, t);
}

export function buildTournamentRulesViewModel(tournament = {}, language = "de", t = null) {
  const tournamentRules = usesTournamentAdditionalRules(tournament);
  return {
    coreRules: buildCoreTournamentRules(tournament, language, t),
    creatorRules: tournamentRules
      ? getAdditionalTournamentRules(tournament, language, t)
      : getAdditionalCreatorRules(tournament, language, t),
    additionalRulesTitleKey: null,
  };
}
